// EVE-Memory-Context-Builder. 1:1 portiert aus V1 useEveMemory.js:
// - formatMemoryContext = V1 getMemoryContext
// - formatRasContext    = V1 getRasContext (mit globalen + user-Regeln)
// - formatSystemRiskContext = V1 getSystemRiskContext
// - formatFullMemoryContext = V1 getFullMemoryContext (kombiniert)

import {
  ALWAYS_CATEGORIES,
  CAUSAL_CATEGORIES,
  MARKET_CATEGORIES,
  detectRasContext,
} from './ras-validation';
import type {
  GlobalRasRule,
  MemoryEntry,
  RasRule,
  SystemRiskSnapshot,
} from '@/types/eve-memory';

/** V1: `[date] fact` joined per newline. */
export function formatMemoryContext(entries: ReadonlyArray<MemoryEntry>): string | null {
  if (entries.length === 0) return null;
  return entries.map((e) => `[${e.date}] ${e.fact}`).join('\n');
}

/**
 * V1 getRasContext:
 * 1. Globale Regeln nach Kontext filtern (ALWAYS / CAUSAL / MARKET)
 * 2. Nach `priority` aufsteigend sortieren
 * 3. User-Regeln immer komplett anhängen
 * 4. Lesbarer Text-Block mit Headers
 */
export function formatRasContext(
  globalRules: ReadonlyArray<GlobalRasRule>,
  userRules: ReadonlyArray<RasRule>,
  userMsg = '',
): string | null {
  const { isCausal, isMarket } = detectRasContext(userMsg);
  const filteredGlobal = globalRules
    .filter((r) => {
      const cat = r.category ?? 'general';
      if (ALWAYS_CATEGORIES.has(cat)) return true;
      if (isCausal && CAUSAL_CATEGORIES.has(cat)) return true;
      if (isMarket && MARKET_CATEGORIES.has(cat)) return true;
      return false;
    })
    .slice()
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

  const lines: string[] = [];
  if (filteredGlobal.length > 0) {
    const causalNote = isCausal ? ' + KAUSAL-ENGINE aktiv' : '';
    const marketNote = isMarket ? ' + MARKT-ANALYSE aktiv' : '';
    lines.push(
      `── UNUMSTÖSSLICHE DENKREGELN (Owner, nach Priorität${causalNote}${marketNote}) ──`,
    );
    for (const r of filteredGlobal) {
      const key = r.rule_key ? ` (${r.rule_key})` : '';
      lines.push(`[CONSTRAINT${key}] ${r.rule}`);
    }
  }
  if (userRules.length > 0) {
    lines.push('── PERSÖNLICHE RAS-REGELN ──');
    for (const r of userRules) {
      lines.push(`[RAS-REGEL] ${r.rule}`);
    }
  }
  return lines.length > 0 ? lines.join('\n') : null;
}

/**
 * V1 getSystemRiskContext — Block für EVE-Prompt aus dem letzten System-Risk-Snapshot.
 */
export function formatSystemRiskContext(snapshot: SystemRiskSnapshot | null): string | null {
  if (!snapshot || !snapshot.systemRiskLevel) return null;
  const lastDate = snapshot.lastRiskSim
    ? new Date(snapshot.lastRiskSim).toLocaleDateString('de-DE')
    : 'unbekannt';
  const pct = snapshot.systemRiskPct != null ? snapshot.systemRiskPct.toFixed(1) : '—';
  return `SYSTEMRISIKO-STATUS (letzte Simulation: ${lastDate}): ${snapshot.systemRiskLevel} | Insolvenz-WSK: ${pct}% | Haupt-Trigger: ${snapshot.topRiskTrigger ?? '—'} | EVE-Empfehlung: ${snapshot.recommendation ?? '—'}`;
}

/**
 * V1 getFullMemoryContext — kombiniert RAS + Memory in einen Block.
 * RAS-Regeln zuerst (höchste Priorität), dann Memory-Erinnerungen.
 */
export function formatFullMemoryContext(input: {
  memories: ReadonlyArray<MemoryEntry>;
  rasRules: ReadonlyArray<RasRule>;
  globalRasRules: ReadonlyArray<GlobalRasRule>;
  userMsg?: string;
}): string | null {
  const memCtx = formatMemoryContext(input.memories);
  const rasCtx = formatRasContext(input.globalRasRules, input.rasRules, input.userMsg ?? '');

  const blocks: string[] = [];
  if (rasCtx) blocks.push(`RAS-REGELN (höchste Priorität):\n${rasCtx}`);
  if (memCtx) blocks.push(`WEITERE ERINNERUNGEN:\n${memCtx}`);
  return blocks.length > 0 ? blocks.join('\n\n') : null;
}

/**
 * V1: extrahiert RAS-Regeln aus der LLM-Antwort (JSON-Liste von Strings).
 * Verarbeitet Markdown-Code-Fences + Fallback auf Quote-Matching.
 */
export function parseAiExtractedRules(rawText: string): string[] {
  let jsonText = rawText
    .trim()
    .replace(/^```(?:json)?\s*\n?/, '')
    .replace(/\n?```\s*$/, '')
    .trim();
  try {
    const parsed = JSON.parse(jsonText);
    if (Array.isArray(parsed)) {
      return parsed.filter((s): s is string => typeof s === 'string' && s.length >= 25);
    }
  } catch {
    // fall through to quoted-string fallback
  }
  const quoted = jsonText.match(/"(?:[^"\\]|\\.)*"/g) ?? [];
  return quoted
    .map((s): string => {
      try {
        return JSON.parse(s) as string;
      } catch {
        return s.replace(/^"|"$/g, '');
      }
    })
    .filter((s): s is string => typeof s === 'string' && s.length >= 25);
}

/**
 * V1 `extractAndSaveMemory`: separates facts in `[RAS-REGEL] ...` from regular facts.
 */
export function splitFactsAndRasRules(facts: string[]): { rules: string[]; plainFacts: string[] } {
  const rules: string[] = [];
  const plainFacts: string[] = [];
  for (const f of facts) {
    if (typeof f !== 'string' || !f.trim()) continue;
    if (f.startsWith('[RAS-REGEL]')) {
      rules.push(f.replace('[RAS-REGEL]', '').trim());
    } else {
      plainFacts.push(f);
    }
  }
  return { rules, plainFacts };
}
